import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, Flower2 } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, formatApiError } from "../../lib/api";
import { PageTitle, Field, TextInput, TextArea, ImageUpload } from "./shared";

const TYPES = [
  { value: "tuber", label: "TUBER" },
  { value: "seedling", label: "SEEDLING" },
  { value: "seed", label: "SEED" },
];

const TypeSelect = ({ value, onChange, testid }) => (
  <select value={value} onChange={onChange} data-testid={testid} className="rounded-full px-4 py-2 bg-cream border border-plum-deep/15 text-sm focus:outline-none focus:border-magenta">
    {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
  </select>
);

const VarietyCard = ({ variety, colours, onDeleted }) => {
  const [data, setData] = useState(variety);
  const [dirty, setDirty] = useState(false);

  const set = (patch) => { setData((d) => ({ ...d, ...patch })); setDirty(true); };

  const save = async () => {
    await api.patch(`/admin/varieties/${variety.id}`, {
      name: data.name, type: data.type, photo: data.photo,
      growing_info: data.growing_info, preferred_colour_id: data.preferred_colour_id,
    });
    setDirty(false);
    toast.success(`${data.name} saved`);
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${data.name}?`)) return;
    await api.delete(`/admin/varieties/${variety.id}`);
    onDeleted(variety.id);
    toast.success("Variety deleted");
  };

  return (
    <div className="bg-warmwhite border border-plum-deep/10 rounded-[1.5rem] p-5" data-testid={`variety-card-${variety.id}`}>
      <div className="flex items-start gap-4">
        <div className="w-20 h-20 rounded-2xl overflow-hidden bg-cream shrink-0 flex items-center justify-center">
          {data.photo ? (
            <img src={imgUrl(data.photo)} alt={data.name} className="w-full h-full object-cover" />
          ) : (
            <Flower2 className="w-8 h-8 text-plum-deep/20" />
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <TextInput value={data.name} onChange={(e) => set({ name: e.target.value })} data-testid={`variety-name-${variety.id}`} className="font-medium" />
          <div className="flex items-center gap-2 flex-wrap">
            <TypeSelect value={data.type} onChange={(e) => set({ type: e.target.value })} testid={`variety-type-${variety.id}`} />
            <select
              value={data.preferred_colour_id || ""}
              onChange={(e) => set({ preferred_colour_id: e.target.value })}
              data-testid={`variety-colour-${variety.id}`}
              className="rounded-full px-4 py-2 bg-cream border border-plum-deep/15 text-sm focus:outline-none focus:border-magenta"
            >
              <option value="">TAG COLOUR: DEFAULT</option>
              {colours.map((c) => <option key={c.id} value={c.id}>TAG: {c.name}</option>)}
            </select>
          </div>
        </div>
      </div>
      <TextArea rows={2} value={data.growing_info || ""} onChange={(e) => set({ growing_info: e.target.value })} placeholder="Growing information…" className="mt-3" data-testid={`variety-info-${variety.id}`} />
      <div className="mt-3 flex items-center gap-3">
        <ImageUpload value={data.photo} onChange={(url) => set({ photo: url })} testid={`variety-photo-${variety.id}`} />
        <div className="ml-auto flex gap-2">
          {dirty && (
            <button onClick={save} data-testid={`variety-save-${variety.id}`} className="inline-flex items-center gap-2 rounded-full bg-forest text-cream px-4 py-2 text-xs uppercase tracking-[0.14em] font-semibold hover:bg-plum">
              <Save className="w-3.5 h-3.5" /> SAVE
            </button>
          )}
          <button onClick={remove} data-testid={`variety-delete-${variety.id}`} className="text-plum-deep/40 hover:text-red-600 p-2" aria-label="Delete variety">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const Varieties = () => {
  const [varieties, setVarieties] = useState(null);
  const [colours, setColours] = useState([]);
  const [newV, setNewV] = useState({ name: "", type: "tuber" });

  useEffect(() => {
    Promise.all([api.get("/admin/varieties"), api.get("/admin/colours")])
      .then(([v, c]) => { setVarieties(v.data); setColours(c.data); })
      .catch(() => setVarieties([]));
  }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!newV.name.trim()) { toast.error("Give the variety a name"); return; }
    try {
      const { data } = await api.post("/admin/varieties", newV);
      setVarieties((l) => [...(l || []), data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewV({ name: "", type: "tuber" });
      toast.success(`${data.name} added to the catalogue`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not add variety");
    }
  };

  return (
    <div data-testid="console-varieties">
      <PageTitle kicker="HERITAGE DAHLIAS · CATALOGUE" title="VARIETY DATABASE" />

      <form onSubmit={add} className="bg-warmwhite border border-plum-deep/10 rounded-[1.5rem] p-5 mb-6 flex flex-wrap items-end gap-3" data-testid="new-variety-form">
        <Field label="VARIETY NAME">
          <TextInput value={newV.name} onChange={(e) => setNewV({ ...newV, name: e.target.value })} placeholder="e.g. CAFÉ AU LAIT" data-testid="new-variety-name" className="w-64" />
        </Field>
        <Field label="TUBER / SEEDLING / SEED">
          <TypeSelect value={newV.type} onChange={(e) => setNewV({ ...newV, type: e.target.value })} testid="new-variety-type" />
        </Field>
        <button type="submit" data-testid="add-variety-btn" className="inline-flex items-center gap-2 rounded-full bg-forest text-cream px-6 py-2.5 text-xs uppercase tracking-[0.16em] font-semibold hover:bg-plum">
          <Plus className="w-4 h-4" /> ADD VARIETY
        </button>
      </form>

      {varieties === null ? (
        <p className="text-plum-deep/60">Loading catalogue…</p>
      ) : varieties.length === 0 ? (
        <p className="text-plum-deep/60" data-testid="varieties-empty">No varieties yet — add your first dahlia above.</p>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {varieties.map((v) => (
            <VarietyCard key={v.id} variety={v} colours={colours} onDeleted={(id) => setVarieties((l) => l.filter((x) => x.id !== id))} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Varieties;
